import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { ADMIN_ROLE_ID, HOUSEKEEPING_ROLE_ID, FRONT_DESK_ROLE_ID } from '@/lib/roles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import PwaInstallButton from './PwaInstallButton';

const adminMenuItems = [
  { path: '/', icon: 'ri-home-5-line', label: 'nav.home' },
  { path: '/casa', icon: 'ri-hotel-line', label: 'nav.casa' },
  { path: '/attendance', icon: 'ri-time-line', label: 'nav.attendance' },
  { path: '/availability', icon: 'ri-calendar-event-line', label: 'nav.availability' },
  { path: '/shifts', icon: 'ri-calendar-schedule-line', label: 'nav.shifts' },
  { path: '/purchase-requests', icon: 'ri-shopping-cart-2-line', label: 'nav.purchaseRequests' },
  { path: '/staff', icon: 'ri-team-line', label: 'nav.staff' },
  { path: '/reports', icon: 'ri-bar-chart-line', label: 'nav.reports' },
  { path: '/profit-loss', icon: 'ri-funds-box-line', label: 'nav.profitLoss' },
  { path: '/goldys-closing', icon: 'ri-cash-line', label: 'nav.goldysClosing' },
  { path: '/receivables', icon: 'ri-bank-card-line', label: 'nav.receivables' },
  { path: '/payables', icon: 'ri-bill-line', label: 'nav.payables' },
  { path: '/contracts', icon: 'ri-file-list-3-line', label: 'nav.contracts' },
  { path: '/reimbursements', icon: 'ri-refund-2-line', label: 'nav.reimbursements' },
  { path: '/daily-pay', icon: 'ri-hand-coin-line', label: 'nav.dailyPay' },
  { path: '/notifications', icon: 'ri-notification-3-line', label: 'nav.notifications' },
  { path: '/messages', icon: 'ri-chat-3-line', label: 'nav.messages' },
  { path: '/settings', icon: 'ri-settings-3-line', label: 'nav.settings' },
  { path: '/account', icon: 'ri-user-settings-line', label: 'nav.account' },
];

const frontDeskMenuItems = [
  { path: '/', icon: 'ri-home-5-line', label: 'nav.home' },
  { path: '/reservations', icon: 'ri-hotel-line', label: 'nav.casa' },
  { path: '/attendance', icon: 'ri-time-line', label: 'nav.attendance' },
  { path: '/availability', icon: 'ri-calendar-event-line', label: 'nav.availability' },
  { path: '/shifts', icon: 'ri-calendar-schedule-line', label: 'nav.shifts' },
  { path: '/purchase-requests', icon: 'ri-shopping-cart-2-line', label: 'nav.purchaseRequests' },
  { path: '/reimbursements', icon: 'ri-refund-2-line', label: 'nav.reimbursements' },
  { path: '/daily-pay', icon: 'ri-hand-coin-line', label: 'nav.dailyPay' },
  { path: '/notifications', icon: 'ri-notification-3-line', label: 'nav.notifications' },
  { path: '/messages', icon: 'ri-chat-3-line', label: 'nav.messages' },
  { path: '/account', icon: 'ri-user-settings-line', label: 'nav.account' },
];

const housekeepingMenuItems = [
  { path: '/', icon: 'ri-home-5-line', label: 'nav.home' },
  { path: '/cleaning', icon: 'ri-hotel-line', label: 'nav.casa' },
  { path: '/attendance', icon: 'ri-time-line', label: 'nav.attendance' },
  { path: '/availability', icon: 'ri-calendar-event-line', label: 'nav.availability' },
  { path: '/shifts', icon: 'ri-calendar-schedule-line', label: 'nav.shifts' },
  { path: '/purchase-requests', icon: 'ri-shopping-cart-2-line', label: 'nav.purchaseRequests' },
  { path: '/reimbursements', icon: 'ri-refund-2-line', label: 'nav.reimbursements' },
  { path: '/daily-pay', icon: 'ri-hand-coin-line', label: 'nav.dailyPay' },
  { path: '/notifications', icon: 'ri-notification-3-line', label: 'nav.notifications' },
  { path: '/messages', icon: 'ri-chat-3-line', label: 'nav.messages' },
  { path: '/account', icon: 'ri-user-settings-line', label: 'nav.account' },
];

export default function Sidebar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = profile?.access_level === 'super_admin';
  const isOperationsManager = profile?.access_level === 'operations_manager';
  const isFrontDesk = profile?.role_id === FRONT_DESK_ROLE_ID;
  const isHousekeeping = profile?.role_id === HOUSEKEEPING_ROLE_ID;

  let menuItems = adminMenuItems;
  if (isOperationsManager) {
    const allowed = new Set(['/','/attendance','/availability','/shifts','/purchase-requests','/daily-pay','/notifications','/messages','/account']);
    if (profile?.permissions.includes('manage_operations')) allowed.add('/casa');
    if (profile?.permissions.includes('view_reports')) allowed.add('/reports');
    menuItems = adminMenuItems.filter((item) => allowed.has(item.path));
  } else if (isFrontDesk) menuItems = frontDeskMenuItems;
  else if (isHousekeeping) menuItems = housekeepingMenuItems;

  const roleLabel = isAdmin ? t('nav.admin') : isOperationsManager ? '運用管理者' : t('nav.teamMember');

  const displayName = profile?.full_name || null;
  const userInitial = displayName ? displayName.charAt(0).toUpperCase() : (user?.email?.charAt(0).toUpperCase() || '?');

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const navigation = (onSelect?: () => void) => <>
    <nav className="flex-1 px-3 py-4 overflow-y-auto">
      <div className="mb-3 flex items-center justify-between gap-2 px-3"><p className="text-xs font-medium uppercase tracking-wider" style={{ color: '#66665F' }}>{t('nav.menu')}</p><LanguageSwitcher /></div>
      <ul className="space-y-1">
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return <li key={item.path}><NavLink to={item.path} onClick={onSelect} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 whitespace-nowrap ${isActive ? '' : 'hover:bg-background-100'}`} style={isActive ? { backgroundColor: '#234638', color: '#FFFEFA' } : { color: '#252824' }}><span className="w-5 h-5 flex items-center justify-center" style={{ color: isActive ? '#FFFEFA' : '#66665F' }}><i className={`${item.icon} text-base`} /></span>{t(item.label)}</NavLink></li>;
        })}
      </ul>
    </nav>
  </>;

  return (<>
    <header className="md:hidden fixed top-0 inset-x-0 h-16 z-40 flex items-center gap-3 px-4 bg-[#FFFEFA] border-b border-[#DED9CC]">
      <button onClick={() => setMobileOpen(true)} className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100" aria-label={t('nav.openMenu')}><i className="ri-menu-line text-xl" /></button>
      <NavLink to="/" className="flex items-center gap-3" aria-label="TOPへ戻る">
        <img src="/assets/peche-et-fleur-logo.jpg" alt="Peche et Fleur" className="w-12 h-11 object-contain" />
        <div><p className="text-sm font-extrabold text-[#252824]">Peche et Fleur</p><p className="text-xs font-bold text-[#234638]">Staff Portal</p></div>
      </NavLink>
    </header>
    {mobileOpen && <div className="md:hidden fixed inset-0 z-50 flex">
      <button className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} aria-label={t('nav.closeMenu')} />
      <aside className="relative w-[286px] h-full flex flex-col bg-[#FFFEFA] shadow-xl">
        <div className="flex items-center justify-between px-4 py-4 border-b border-[#DED9CC]"><NavLink to="/" onClick={() => setMobileOpen(false)} className="font-bold">Peche et Fleur Staff Portal</NavLink><button onClick={() => setMobileOpen(false)} className="w-8 h-8"><i className="ri-close-line text-xl" /></button></div>
        {navigation(() => setMobileOpen(false))}
      </aside>
    </div>}
    <aside className="fixed left-0 top-0 h-full min-w-[268px] w-[268px] hidden md:flex flex-col z-30" style={{ backgroundColor: '#FFFEFA', borderRight: '1px solid #DED9CC' }}>
      <NavLink to="/" className="flex items-center gap-3 shrink-0 hover:bg-background-50 transition-colors" style={{ padding: '14px 14px 16px', minHeight: '96px', borderBottom: '1px solid #DED9CC' }} aria-label="TOPへ戻る">
        <img
          src="/assets/peche-et-fleur-logo.jpg"
          alt="Peche et Fleur"
          className="w-[66px] h-[58px] flex-shrink-0 object-contain rounded-md bg-white"
        />
        <div className="min-w-0 flex-1">
          <h1 style={{ fontWeight: 800, color: '#252824', fontSize: '15px', lineHeight: '1.25' }}>Peche et Fleur</h1>
          <p style={{ fontWeight: 700, fontSize: '13px', lineHeight: '1.25', color: '#234638', marginTop: '3px' }}>Staff Portal</p>
        </div>
      </NavLink>

      {navigation()}

      <div className="px-4 py-4 border-t" style={{ borderColor: '#DED9CC' }}>
        <PwaInstallButton />
        <div className="flex items-center gap-3 px-2 py-2 rounded-lg" style={{ backgroundColor: '#F0EDE5' }}>
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#A8843A' }}>
            <span className="text-xs font-semibold" style={{ color: '#FFFEFA' }}>{userInitial}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" style={{ color: '#252824' }}>{displayName}</p>
            <p className="text-xs" style={{ color: '#66665F' }}>{roleLabel}</p>
          </div>
          <button
            onClick={handleSignOut}
            className="w-7 h-7 flex items-center justify-center rounded-md transition-colors cursor-pointer"
            style={{ color: '#66665F' }}
            title={t('nav.logout')}
          >
            <i className="ri-logout-box-r-line text-sm"></i>
          </button>
        </div>
      </div>
    </aside>
  </>);
}
